/**
 * C05. Day-1 Hook — first-launch gift, 7-day login calendar, tutorial boss.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §C05 ("Day-1 首日新手獎勵設計").
 * Pillar: Retention (D1 → D7 漏斗 — 命門, per market-insights memory).
 *
 * Purely a *state machine* over `PlayerProgress.dailyHook`. UI lives in:
 *   - dayone-banner.ts   (home page entry banner)
 *   - dayone-calendar.ts (7-day claim grid)
 * Tutorial boss def lives in `src/game/ai/states/tutorial-boss.ts` (built on
 * G02 FSM, opt-in `BOSS_TUTORIAL_POOL` — never wired into legacy boss pools
 * so the existing 9-boss balance is untouched).
 *
 * The 7-day calendar uses *real calendar days* (UTC) measured from
 * `dailyHook.firstLaunchAt`. Day-N becomes claimable once the local date
 * is >= firstLaunchAt + (N-1) days. Skipped days do NOT reset progress
 * (forgiving for kids); they are simply claimable as soon as the date is
 * reached. The full claim arc is 7 day-buckets, no streak break.
 */

import type { PlayerProgress } from '../shared/types';
import { addFreeXP, saveProgress } from '../shared/progress';

// ── Reward table ────────────────────────────────────────────────────────────

/** Reward descriptor for one day in the 7-day login calendar. */
export interface DayReward {
  /** 1..7 — the day number in the login arc. */
  day: number;
  /** freeXP granted on claim. D7 is the "★" tier. */
  freeXP: number;
  /**
   * Optional skill id to also unlock on claim. Currently only D7 unlocks
   * `tutorial-boss-trophy` (cosmetic-style flag — does NOT alter combat;
   * acts as a "you finished week-one" badge surfaced in mistake-book / dashboard
   * if those surfaces opt-in later. Pure-data, no skill-tree effect).
   */
  bonus?: string;
  /** True for the day-7 milestone (UI highlights with ★ + glow). */
  star?: boolean;
}

/**
 * Per-day reward table. Tunable in one place — keep totals additive to
 * something motivating (~1k freeXP across the full week) without dwarfing
 * what kids earn from a single lesson (~150-300 XP).
 */
export const DAY_REWARDS: readonly DayReward[] = [
  { day: 1, freeXP: 100 },
  { day: 2, freeXP: 150 },
  { day: 3, freeXP: 200 },
  { day: 4, freeXP: 250 },
  { day: 5, freeXP: 300 },
  { day: 6, freeXP: 400 },
  { day: 7, freeXP: 500, bonus: 'tutorial-boss-trophy', star: true },
];

// ── First-launch gift ───────────────────────────────────────────────────────

/** freeXP awarded by the first-launch gift overlay. */
export const FIRST_LAUNCH_GIFT_XP = 200;

/**
 * Built-in skill id that gets unlocked alongside the first-launch gift.
 * `spread-shot` is the kid's first owned skill (faction = 'builtin', cost 0
 * in SKILL_CATALOG) — adding it to `unlockedSkills` ensures it shows up in
 * the loadout picker even before the player has any faction XP. We deliberately
 * pick a built-in skill so the gift does NOT short-circuit the faction-XP
 * unlock economy (CLAUDE.md: 學習與遊戲分離 — skills earned via XP must stay
 * earned via XP).
 */
export const FIRST_LAUNCH_GIFT_SKILL = 'spread-shot';

/**
 * Tutorial-boss completion reward — 200 freeXP, awarded once.
 */
export const TUTORIAL_BOSS_XP = 200;

// ── State machine ───────────────────────────────────────────────────────────

/** Read or initialise the dailyHook sub-object (idempotent, mutates `p`). */
function getDailyHook(p: PlayerProgress): NonNullable<PlayerProgress['dailyHook']> {
  if (!p.dailyHook) {
    p.dailyHook = { firstLaunchAt: 0, claimedDays: [], tutorialBossCompleted: false };
  }
  return p.dailyHook;
}

/**
 * Seed `firstLaunchAt` the first time the player lands on home after the v18
 * migration (or, for fresh accounts, the first ever home render). Idempotent:
 * once seeded, subsequent calls are no-ops.
 *
 * `nowMs` is passed in for deterministic tests; defaults to `Date.now()`.
 * Returns true on the first call (caller can use this to show the first-launch
 * gift overlay), false thereafter.
 */
export function seedFirstLaunchIfNeeded(p: PlayerProgress, nowMs: number = Date.now()): boolean {
  const dh = getDailyHook(p);
  if (dh.firstLaunchAt > 0) return false;
  dh.firstLaunchAt = nowMs;
  return true;
}

/**
 * True iff the first-launch gift hasn't been claimed yet (claim = D1 in the
 * 7-day calendar). We use D1's claim status as the single source of truth
 * for "has the player accepted the welcome gift?" so the gift overlay and
 * D1 calendar tile stay in sync — claiming the gift in the overlay marks
 * D1 claimed, hiding both for subsequent home visits.
 */
export function isFirstLaunchGiftPending(p: PlayerProgress): boolean {
  const dh = getDailyHook(p);
  if (dh.firstLaunchAt === 0) return true; // not yet seeded
  return !dh.claimedDays.includes(1);
}

/**
 * Number of calendar days elapsed between `firstLaunchAt` and `nowMs`.
 * Day-1 of the arc corresponds to elapsed === 0 (the first launch day).
 * Day-2 unlocks once the UTC date has advanced by one day, etc.
 */
function elapsedDays(firstLaunchAt: number, nowMs: number): number {
  if (firstLaunchAt === 0) return 0;
  const start = new Date(firstLaunchAt);
  const now = new Date(nowMs);
  // Truncate both to UTC midnight so DST / time-of-day don't skew the count.
  const startKey = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const nowKey = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffMs = nowKey - startKey;
  return Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
}

/**
 * Highest day number (1..7) currently claimable today.
 *
 * Returns 0 if the player hasn't been seeded yet (caller should call
 * `seedFirstLaunchIfNeeded` first, which advances eligibility to 1).
 */
export function highestClaimableDay(p: PlayerProgress, nowMs: number = Date.now()): number {
  const dh = getDailyHook(p);
  if (dh.firstLaunchAt === 0) return 0;
  return Math.min(7, 1 + elapsedDays(dh.firstLaunchAt, nowMs));
}

/** True iff `day` (1..7) is currently claimable AND not yet claimed. */
export function canClaimDay(p: PlayerProgress, day: number, nowMs: number = Date.now()): boolean {
  if (day < 1 || day > 7) return false;
  const dh = getDailyHook(p);
  if (dh.claimedDays.includes(day)) return false;
  return day <= highestClaimableDay(p, nowMs);
}

/**
 * Claim a single day. Mutates `p` and persists via `saveProgress`.
 * Returns the awarded reward, or `null` if the day was not claimable.
 *
 * Side effects on success:
 *   1. Add `reward.freeXP` to `p.freeXP` (via `addFreeXP` so dailyXP is also bumped)
 *   2. Push day to `claimedDays`
 *   3. If D1 and the first-launch gift hasn't been awarded, ALSO grant
 *      FIRST_LAUNCH_GIFT_XP + unlock FIRST_LAUNCH_GIFT_SKILL. (D1 IS the
 *      first-launch gift — design ties them together so the kid only sees
 *      one "🎁 領禮包!" moment, not two competing overlays.)
 *   4. If `reward.bonus` is set, push the bonus id to `unlockedSkills` (once)
 *   5. Persist via `saveProgress(p)`.
 */
export function claimDay(p: PlayerProgress, day: number, nowMs: number = Date.now()): DayReward | null {
  if (!canClaimDay(p, day, nowMs)) return null;
  const reward = DAY_REWARDS.find((r) => r.day === day);
  if (!reward) return null;

  const dh = getDailyHook(p);
  let bonusXp = 0;
  if (day === 1) {
    // First-launch gift bundle (only the FIRST time D1 is claimed for this
    // player — guarded by claimedDays check above).
    bonusXp += FIRST_LAUNCH_GIFT_XP;
    if (!p.unlockedSkills.includes(FIRST_LAUNCH_GIFT_SKILL)) {
      p.unlockedSkills.push(FIRST_LAUNCH_GIFT_SKILL);
    }
  }
  addFreeXP(p, reward.freeXP + bonusXp);
  if (reward.bonus && !p.unlockedSkills.includes(reward.bonus)) {
    p.unlockedSkills.push(reward.bonus);
  }
  dh.claimedDays.push(day);
  saveProgress(p);
  // C02 Battle Pass hook — emit so the season-pass listener can credit
  // seasonXP. We dispatch BEFORE returning so the event fires within the
  // same microtask the caller drives. Wrapped in try because jsdom test
  // envs may not have CustomEvent shim — state already mutated, ignore.
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('ef:dayone-claimed', { detail: { day, freeXP: reward.freeXP } }));
    } catch {
      // ignore
    }
  }
  return reward;
}

/**
 * Mark the tutorial boss as completed and grant TUTORIAL_BOSS_XP (once).
 * Idempotent — subsequent calls return false and grant nothing.
 *
 * Persists via `saveProgress`. Called by the battle scene after the kid
 * defeats `tutorial-boss` (see src/game/ai/states/tutorial-boss.ts). The
 * scene wire-up is deferred to a follow-up task; for now the function is
 * exposed so the home banner can render a "尚未完成" / "已完成" badge and
 * unit tests can drive the state directly.
 */
export function completeTutorialBoss(p: PlayerProgress): boolean {
  const dh = getDailyHook(p);
  if (dh.tutorialBossCompleted) return false;
  dh.tutorialBossCompleted = true;
  addFreeXP(p, TUTORIAL_BOSS_XP);
  saveProgress(p);
  return true;
}

/**
 * Convenience snapshot used by the banner + calendar UIs to render in one go
 * without each surface re-deriving the same fields.
 */
export interface DayOneHookSnapshot {
  /** Seeded? — false means the home page should call `seedFirstLaunchIfNeeded`. */
  seeded: boolean;
  /** True iff the first-launch overlay should be shown right now. */
  showGiftOverlay: boolean;
  /** Highest day currently claimable (0 if not seeded). */
  todayDay: number;
  /** Days already claimed (subset of 1..7). */
  claimedDays: number[];
  /** Days that are claimable RIGHT NOW (subset of 1..7, excludes already-claimed). */
  pendingDays: number[];
  /** Whether the must-win tutorial boss is still available. */
  tutorialBossPending: boolean;
  /** True iff every day has been claimed AND tutorial boss completed — banner can hide. */
  fullyComplete: boolean;
}

export function snapshotHook(p: PlayerProgress, nowMs: number = Date.now()): DayOneHookSnapshot {
  const dh = getDailyHook(p);
  const seeded = dh.firstLaunchAt > 0;
  const today = seeded ? highestClaimableDay(p, nowMs) : 0;
  const pending: number[] = [];
  for (let d = 1; d <= today; d++) {
    if (!dh.claimedDays.includes(d)) pending.push(d);
  }
  return {
    seeded,
    showGiftOverlay: seeded && isFirstLaunchGiftPending(p),
    todayDay: today,
    claimedDays: [...dh.claimedDays].sort((a, b) => a - b),
    pendingDays: pending,
    // NOTE (2026-05-30): the 教學魔王 CTA was removed from the home banner per boss
    // decision「直接拿掉所有教學 gate」(see dayone-banner.ts) so the kid can never be
    // routed into the sticky tutorial boss fight. These two flags are kept as-is for
    // back-compat with the banner-priority queue + existing tests; they never gate
    // gameplay (the only consumer is the banner's auto-hide / priority logic).
    tutorialBossPending: !dh.tutorialBossCompleted,
    fullyComplete: dh.claimedDays.length >= 7 && dh.tutorialBossCompleted,
  };
}
